// src/components/EMSAlarms.js
import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../Tools/Topbar';
import LeftSide from '../Tools/LeftSidebar';
import Pagination from '../Tools/Pagination';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import axiosInstance from '../../../api';
import { format, parseISO } from 'date-fns';
import ReasonModal from './Alarm/ReasonModal'; // Adjust path as needed

// Fetch date/time format settings
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting');
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const EMSAlarms = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ type: '' });

  // Modal state
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState(null);
  const [modalMode, setModalMode] = useState('acknowledge'); // 'acknowledge' or 'view'
  const [acknowledging, setAcknowledging] = useState(false);

  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Format created_at timestamp
  // Correct & robust UTC-safe time formatter (same as CasesTab)
const formatAlarmTime = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
    const timeFormat = sessionStorage.getItem('time_format') || '24';

    // Map your backend formats to date-fns
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    let utcDate;

    if (dateString.includes('T')) {
      // ISO format: 2025-10-27T16:27:19.000000Z
      utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    } else {
      // MySQL format: 2025-10-27 16:27:19
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
    }

    // Extract UTC parts manually to avoid local timezone shift
    const formatted = format(
      new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      ),
      formatString
    );

    return formatted;
  } catch (err) {
    console.error('Error formatting alarm time:', err, dateString);
    return 'Invalid Date';
  }
};

  // Fetch alarms from API
  const fetchAlarms = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/ems/alerts', {
        params: {
          user_id: sessionStorage.getItem('user_id'),
          type: filters.type,
        },
      });

      const apiData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setAlarms(apiData);
    } catch (error) {
      console.error('Error fetching alarms:', error);
      showAlert('Failed to load alarms.', false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlarms();
  }, [filters.type]);

  const showAlert = (message, isSuccess = true) => {
    alert(`${isSuccess ? 'Success' : 'Error'}: ${message}`);
  };

  // Open modal in acknowledge or view mode
  const openReasonModal = (alarm, mode = 'acknowledge') => {
    setSelectedAlarm(alarm);
    setModalMode(mode);
    setShowReasonModal(true);
  };

  const closeReasonModal = () => {
    setShowReasonModal(false);
    setSelectedAlarm(null);
    setModalMode('acknowledge');
  };

  // Submit acknowledgment
  const handleAcknowledgeWithReason = async (alertId, formData) => {
    setAcknowledging(true);
    try {
      const response = await axiosInstance.post(
        `/ems/alarm-acknowledge/${alertId}`,
        new URLSearchParams({
          resolution_message: formData.resolution_message,
        }).toString(),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }
      );

      if (response.data?.status || response.status === 200) {
        setAlarms((prev) =>
          prev.map((alarm) =>
            alarm.id === alertId
              ? {
                  ...alarm,
                  acknowledge_status: 1,
                  resolution_message: formData.resolution_message,
                  acknowledged_at: new Date().toISOString(),
                }
              : alarm
          )
        );
        showAlert('Alarm acknowledged successfully.');
        closeReasonModal();
        await fetchAlarms();
      } else {
        showAlert(response.data?.message || 'Failed to acknowledge.', false);
      }
    } catch (error) {
      console.error('Error acknowledging alarm:', error);
      const msg = error.response?.data?.message || 'Network error.';
      showAlert(msg, false);
    } finally {
      setAcknowledging(false);
    }
  };

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 10;
  const total = alarms.length;
  const paginatedAlarms = alarms.slice((page - 1) * limit, page * limit);

  const handlePageChange = (newPage) => setPage(newPage);
  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClear = () => setFilters({ type: '' });

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <TopBar toggleSidebar={toggleSidebar} />
        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            <div className="card w-100 position-relative overflow-hidden">
              <div className="px-4 py-3">
                <h5 className="card-title fw-semibold mb-0 lh-sm">System Alarms</h5>
                <p className="card-subtitle mb-0 mt-1">
                  View and manage real-time alerts
                </p>
              </div>

              <div className="card-body p-4">
                {/* Filter */}
                <div className="filter-search mb-3">
                  <h6 className="px-2 mb-0">Filter</h6>
                  <div className="filter-panel w-100">
                    <form className="d-flex flex-wrap align-items-end gap-2">
                      <div style={{ maxWidth: '250px' }}>
                        <select name="type" value={filters.type} onChange={handleFilterChange} className="form-control form-select"
                        >
                          <option value="">All Types</option>
                          <option value="Employee Auto Assignment">Employee Auto Assignment</option>
                          <option value="Vehicle Checklist">Vehicle Checklist</option>
                          <option value="Vehicle Auto Assignment">Vehicle Auto Assignment</option>
                          <option value="Equipment Checklist">Equipment Checklist</option>
                          <option value="Case Acknowledgement Success">Case Acknowledgement</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleClear}
                        className="btn btn-outline-primary"
                        style={{ minWidth: '100px' }}
                      >
                        Clear
                      </button>
                    </form>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive rounded-2">
                  <table className="table text-nowrap mb-0 align-middle">
                    <thead className="text-dark fs-4 bg-light">
                      <tr>
                        <th className="fw-semibold">Time</th>
                        <th className="fw-semibold">Severity</th>
                        <th className="fw-semibold">Message</th>
                        <th className="fw-semibold">Type</th>
                        <th className="fw-semibold">Status</th>
                        <th className="fw-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">Loading...</td>
                        </tr>
                      ) : paginatedAlarms.length > 0 ? (
                        paginatedAlarms.map((alarm) => {
                          const isAcknowledged = alarm.acknowledge_status === 1;

                          return (
                            <tr key={alarm.id}>
                              <td>
                                <p className="mb-0 fw-normal">
                                  {formatAlarmTime(alarm.created_at)}
                                </p>
                              </td>
                              <td>
                                <p className="mb-0 fw-normal d-flex align-items-center">
                                  <span
                                    className={`round-8 me-2 d-inline-block rounded-circle ${
                                      alarm.severity === 'High'
                                        ? 'bg-danger'
                                        : alarm.severity === 'Medium'
                                        ? 'bg-warning'
                                        : 'bg-success'
                                    }`}
                                    style={{ width: '8px', height: '8px' }}
                                  ></span>
                                  {alarm.severity}
                                </p>
                              </td>
                              <td>
                                <p
                                  className="mb-0 fw-normal"
                                  style={{
                                    maxWidth: '300px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {alarm.description}
                                </p>
                              </td>
                              <td>
                                <p className="mb-0 fw-normal">{alarm.module_name}</p>
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    isAcknowledged
                                      ? 'bg-success-subtle text-success'
                                      : 'bg-warning-subtle text-warning'
                                  }`}
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  {isAcknowledged ? 'Success' : 'Pending'}
                                </span>
                              </td>
                              <td className="text-center">
                                {isAcknowledged ? (
                                  <button
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => openReasonModal(alarm, 'view')}
                                    title="View resolution reason"
                                  >
                                    Reason
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => openReasonModal(alarm, 'acknowledge')}
                                    disabled={acknowledging}
                                    title="Acknowledge alarm"
                                  >
                                    Acknowledge
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">No alarms found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                  <div className="mt-4">
                    <Pagination
                      total={total}
                      limit={limit}
                      page={page}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Reason Modal */}
            <ReasonModal
              isOpen={showReasonModal}
              onClose={closeReasonModal}
              onSubmit={handleAcknowledgeWithReason}
              alarm={selectedAlarm}
              loading={acknowledging}
              mode={modalMode}
            />
          </div>
        </div>
      </div>

      <div className="dark-transparent sidebartoggler" onClick={toggleSidebar}></div>
    </div>
  );
};

export default EMSAlarms;